// documentStudioApi.js — shared API client + helpers for WAVEY Document Studio.
//
// Talks to the existing authenticated backend: user calls go to /api/studio,
// admin calls to /api/admin/templates. Both attach the same bearer tokens the
// rest of the app uses (authToken for users, adminToken for admins) so there is
// no new auth surface.
import { getApiBaseUrl } from "./baseUrl";

const userToken = () =>
  localStorage.getItem("authToken") || sessionStorage.getItem("authToken") || "";
const adminToken = () => localStorage.getItem("adminToken") || "";

const request = async (path, { method = "GET", body, token = userToken(), raw = false } = {}) => {
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  let payload = body;
  if (body && !raw) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }
  const res = await fetch(`${getApiBaseUrl()}${path}`, { method, headers, body: payload });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const error = new Error(data?.message || "Request failed.");
    error.status = res.status;
    error.data = data;
    throw error;
  }
  return data;
};

// --- user-facing Studio API -------------------------------------------------

export const studioApi = {
  meta: () => request("/api/studio/meta"),
  placeholders: () => request("/api/studio/placeholders"),

  listTemplates: () => request("/api/studio/templates"),
  previewTemplate: (id) => request(`/api/studio/templates/${id}/preview`),

  createDocument: (payload) => request("/api/studio/documents", { method: "POST", body: payload }),
  listDocuments: (filter = "mine", q = "") =>
    request(`/api/studio/documents?filter=${encodeURIComponent(filter)}${q ? `&q=${encodeURIComponent(q)}` : ""}`),
  getDocument: (id) => request(`/api/studio/documents/${id}`),
  saveDocument: (id, payload) => request(`/api/studio/documents/${id}`, { method: "PUT", body: payload }),
  submitDocument: (id, payload = {}) =>
    request(`/api/studio/documents/${id}/submit`, { method: "POST", body: payload }),
  saveAsTemplate: (id, payload) =>
    request(`/api/studio/documents/${id}/template`, { method: "POST", body: payload }),
  listVersions: (id) => request(`/api/studio/documents/${id}/versions`),
  getVersion: (id, version) => request(`/api/studio/documents/${id}/versions/${version}`),
  createMilestone: (id, payload) => request(`/api/studio/documents/${id}/versions`, { method: "POST", body: payload }),
  restoreVersion: (id, version, reason = "") => request(`/api/studio/documents/${id}/versions/${version}/restore`, { method: "POST", body: { reason } }),
  addReviewThread: (id, payload) => request(`/api/studio/documents/${id}/review-threads`, { method: "POST", body: payload }),
  updateReviewThread: (id, threadId, payload) => request(`/api/studio/documents/${id}/review-threads/${threadId}`, { method: "PATCH", body: payload }),
  shareDocument: (id, userId, permission = "view") =>
    request(`/api/studio/documents/${id}/collaborators`, { method: "POST", body: { userId, permission } }),
  shareDocumentWithOffice: (id, office) =>
    request(`/api/studio/documents/${id}/office-grants`, { method: "POST", body: office }),
  exportDocument: (id) => request(`/api/studio/documents/${id}/export`),

  importDocument: ({ kind, title, content, documentType, classification }) =>
    request("/api/studio/import", {
      method: "POST",
      body: { kind, title, content, documentType, classification },
    }),
  importPdf: (file, { title, documentType, classification } = {}) => {
    const body = new FormData();
    body.append("file", file);
    body.append("kind", "pdf");
    body.append("title", title || file.name.replace(/\.pdf$/i, ""));
    body.append("documentType", documentType || "");
    body.append("classification", classification || "Restricted");
    return request("/api/studio/import", { method: "POST", body, raw: true });
  },
  importPresentation: (file, { title, documentType, classification } = {}) => {
    const body = new FormData();
    body.append("file", file);
    body.append("kind", "presentation");
    body.append("title", title || file.name.replace(/\.pptx$/i, ""));
    body.append("documentType", documentType || "Presentation");
    body.append("classification", classification || "Restricted");
    return request("/api/studio/import", { method: "POST", body, raw: true });
  },
  getBinary: async (id) => {
    const res = await fetch(`${getApiBaseUrl()}/api/studio/documents/${id}/binary`, { headers: { Authorization: `Bearer ${userToken()}` } });
    if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.message || "Failed to load file.");
    return res.blob();
  },
  getPresentationPreview: async (id) => {
    const res = await fetch(`${getApiBaseUrl()}/api/studio/documents/${id}/preview`, { headers: { Authorization: `Bearer ${userToken()}` } });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      const error = new Error(data?.message || "Failed to load presentation preview.");
      error.status = res.status;
      error.data = data;
      throw error;
    }
    return res.blob();
  },
  retryPresentationPreview: (id) => request(`/api/studio/documents/${id}/preview/retry`, { method: "POST" }),
  uploadPresentationVersion: (id, file, label = "") => {
    const body = new FormData();
    body.append("file", file);
    body.append("label", label);
    return request(`/api/studio/documents/${id}/presentation-versions`, { method: "POST", body, raw: true });
  },
  getPresentationEditorStatus: (id) => request(`/api/studio/documents/${id}/office-editor/config?probe=1`),
  getPresentationEditorConfig: (id) => request(`/api/studio/documents/${id}/office-editor/config`),
  saveSignedPdf: (id, file, placementCount = 0) => {
    const body = new FormData();
    body.append("file", file, `studio-signed-${Date.now()}.pdf`);
    body.append("placementCount", String(placementCount));
    return request(`/api/studio/documents/${id}/signed-version`, { method: "POST", body, raw: true });
  },
  getSigningAssets: () => request("/api/users/me/signing-assets"),
};

// --- admin Template Manager API ---------------------------------------------

export const templateAdminApi = {
  list: () => request("/api/admin/templates", { token: adminToken() }),
  get: (id) => request(`/api/admin/templates/${id}`, { token: adminToken() }),
  create: (payload) => request("/api/admin/templates", { method: "POST", body: payload, token: adminToken() }),
  update: (id, payload) => request(`/api/admin/templates/${id}`, { method: "PUT", body: payload, token: adminToken() }),
  publish: (id, changeNote) =>
    request(`/api/admin/templates/${id}/publish`, { method: "POST", body: { changeNote }, token: adminToken() }),
  unpublish: (id) => request(`/api/admin/templates/${id}/unpublish`, { method: "POST", token: adminToken() }),
  newVersion: (id, changeNote) =>
    request(`/api/admin/templates/${id}/versions`, { method: "POST", body: { changeNote }, token: adminToken() }),
  retire: (id) => request(`/api/admin/templates/${id}/retire`, { method: "POST", token: adminToken() }),
  remove: (id) => request(`/api/admin/templates/${id}`, { method: "DELETE", token: adminToken() }),
};

// --- helpers ----------------------------------------------------------------

export const STUDIO_STATE_TONES = {
  Draft: "draft",
  Submitted: "pending",
  "Under Review": "pending",
  "Corrections Requested": "warning",
  Approved: "success",
  Rejected: "danger",
  Finalized: "success",
  Archived: "muted",
};

export const formatStudioDate = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return "—";
  }
};

export const filterStudioDocuments = (documents, filters) => {
  const search = String(filters.search || "").trim().toLowerCase();
  const sender = String(filters.sender || "").trim().toLowerCase();
  const service = String(filters.service || "").trim().toLowerCase();
  const from = filters.from ? new Date(`${filters.from}T00:00:00`) : null;
  const to = filters.to ? new Date(`${filters.to}T23:59:59.999`) : null;

  return documents.filter((doc) => {
    const searchable = [
      doc.fileName, doc.documentType, doc.studioState, doc.uploadedBy,
      doc.uploadedByRole, doc.unit, doc.unitBranch, doc.branch, doc.subBranch,
    ].filter(Boolean).join(" ").toLowerCase();
    const created = new Date(doc.createdAt || doc.updatedAt || 0);
    const senderText = `${doc.uploadedBy || ""} ${doc.uploadedByRole || ""}`.toLowerCase();
    const serviceText = [doc.unit, doc.unitBranch, doc.branch, doc.subBranch]
      .filter(Boolean).join(" ").toLowerCase();

    return (!search || searchable.includes(search)) &&
      (!filters.status || doc.studioState === filters.status) &&
      (!sender || senderText.includes(sender)) &&
      (!service || serviceText.includes(service)) &&
      (!from || created >= from) &&
      (!to || created <= to);
  });
};

export const getStudioDocumentRoute = (doc) => {
  const kind = String(doc?.studio?.kind || "word").toLowerCase();
  const routeKind = kind === "spreadsheet" ? "sheet" : kind === "pdf" ? "pdf" : kind === "presentation" ? "presentation" : "word";
  return `/studio/${routeKind}/${doc?._id}`;
};

// Word editor: HTML ⇄ plain-text helpers for the toolbar + PDF export.
export const htmlToPlainText = (html = "") =>
  String(html)
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|h[1-6]|li|tr)>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

export default { studioApi, templateAdminApi, getStudioDocumentRoute };
